import React, { useCallback, useEffect, useMemo, useState } from 'react';
import http from '../api/http';
import WideModal from './WideModal';
import { openWithAuth, buildMinioProxyUrl } from '../utils/authedResource';

const normalizePrefix = (prefix) => {
  const raw = String(prefix || '').trim();
  if (!raw) return '';
  return raw.replace(/^\/+|\/+$/g, '');
};

const isDirRecord = (r) => {
  const mime = String(r?.mimeType || '').toLowerCase();
  if (mime === 'application/x-directory') return true;
  const objectName = String(r?.objectName || '');
  return objectName.endsWith('/');
};

const guessKind = (record) => {
  const mime = String(record?.mimeType || '').toLowerCase();
  const name = String(record?.fileName || '').toLowerCase();
  if (mime.startsWith('video/')) return 'video';
  if (mime === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  if (name.endsWith('.ppt') || name.endsWith('.pptx')) return 'ppt';
  if (mime.includes('powerpoint') || mime.includes('presentation')) return 'ppt';
  return '';
};

const getDirectEntries = (allRecords, prefix) => {
  const current = normalizePrefix(prefix);
  const basePrefix = current ? `${current}/` : '';
  const folders = new Set();
  const files = [];

  (allRecords || []).forEach((r) => {
    const objectName = String(r?.objectName || '').replace(/^\/+/, '');
    if (!objectName) return;
    if (basePrefix && !objectName.startsWith(basePrefix)) return;

    const rest = basePrefix ? objectName.slice(basePrefix.length) : objectName;
    if (!rest) return;

    // folder markers or nested children: extract first segment
    const firstSeg = rest.split('/').filter(Boolean)[0] || '';
    if (!firstSeg) return;

    // If nested (has / beyond firstSeg), it's in a subfolder
    const hasNested = rest.includes('/') && !rest.endsWith('/') && rest.split('/').filter(Boolean).length > 1;

    if (isDirRecord(r) || rest.endsWith('/')) {
      folders.add(firstSeg);
      return;
    }

    if (rest.includes('/')) {
      // file under deeper folder
      folders.add(firstSeg);
      return;
    }

    if (!hasNested) {
      files.push({ ...r, objectName });
    }
  });

  const folderList = Array.from(folders).sort((a, b) => a.localeCompare(b, 'zh-Hans-CN'));
  const fileList = files.sort((a, b) => String(a.fileName || '').localeCompare(String(b.fileName || ''), 'zh-Hans-CN'));
  return { folders: folderList, files: fileList };
};

export default function FileManagerSelectModal({
  isOpen,
  onClose,
  onSelect,
  title = '从文件管理选择教材',
  initialPrefix = '培训资料',
  allowedKinds = ['pdf', 'ppt', 'video'],
  addToast
}) {
  const [prefix, setPrefix] = useState(() => normalizePrefix(initialPrefix));
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    setPrefix(normalizePrefix(initialPrefix));
  }, [isOpen, initialPrefix]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await http.get('/files', { params: { prefix } });
      const list = Array.isArray(res.data?.records) ? res.data.records : [];
      setRecords(list);
    } catch (e) {
      console.error('[file-select] fetch list failed:', e);
      addToast?.(e?.response?.data?.error || '获取文件列表失败', 'error');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [addToast, prefix]);

  useEffect(() => {
    if (!isOpen) return;
    fetchList();
  }, [fetchList, isOpen]);

  const { folders, files } = useMemo(() => getDirectEntries(records, prefix), [records, prefix]);

  const breadcrumbs = useMemo(() => {
    const parts = String(prefix || '').split('/').filter(Boolean);
    const items = [{ label: '根目录', value: '' }];
    parts.forEach((seg, idx) => {
      const p = parts.slice(0, idx + 1).join('/');
      items.push({ label: seg, value: p });
    });
    return items;
  }, [prefix]);

  const isAllowed = useCallback((r) => {
    const kind = guessKind(r);
    if (!kind) return false;
    return (allowedKinds || []).includes(kind);
  }, [allowedKinds]);

  const openFile = useCallback((r) => {
    const url = buildMinioProxyUrl(r?.objectName, { filename: r?.fileName });
    if (!url) {
      addToast?.('无法打开该文件', 'error');
      return;
    }
    openWithAuth(url);
  }, [addToast]);

  const selectFile = useCallback((r) => {
    const kind = guessKind(r);
    if (!kind || !(allowedKinds || []).includes(kind)) {
      addToast?.('仅支持选择 PDF / PPT / 视频 文件', 'warning');
      return;
    }
    onSelect?.({
      objectName: r.objectName,
      fileName: r.fileName,
      mimeType: r.mimeType || ''
    });
    onClose?.();
  }, [addToast, allowedKinds, onClose, onSelect]);

  const enterFolder = (folderName) => {
    const current = normalizePrefix(prefix);
    const next = current ? `${current}/${folderName}` : folderName;
    setPrefix(next);
  };

  const goToPrefix = (p) => {
    setPrefix(normalizePrefix(p));
  };

  const rowStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: 12,
    alignItems: 'center',
    padding: '10px 12px',
    border: '1px solid var(--border-color)',
    borderRadius: 10,
    background: '#fff'
  };

  const btn = (variant = 'secondary') => ({
    padding: '.45rem .8rem',
    fontSize: 13,
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: 500,
    border: '1px solid',
    background: variant === 'primary' ? 'var(--brand-primary)' : 'var(--bg-tertiary)',
    color: variant === 'primary' ? '#fff' : 'var(--text-primary)',
    borderColor: variant === 'primary' ? 'var(--brand-primary)' : 'var(--border-color)'
  });

  return (
    <WideModal isOpen={isOpen} onClose={onClose} title={title} size="wide">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          当前位置：
          {breadcrumbs.map((b, idx) => (
            <span key={b.value || 'root'}>
              {idx > 0 ? ' / ' : ''}
              <a
                href="#nav"
                onClick={(e) => {
                  e.preventDefault();
                  goToPrefix(b.value);
                }}
                style={{ color: 'var(--brand-primary)' }}
              >
                {b.label}
              </a>
            </span>
          ))}
        </div>

        {loading ? <div>正在加载...</div> : null}

        {!loading && folders.length === 0 && files.length === 0 ? (
          <div style={{ padding: 12, border: '1px dashed var(--border-color)', borderRadius: 10, color: 'var(--text-secondary)' }}>
            当前目录为空。
          </div>
        ) : null}

        {folders.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 12, color: '#64748b' }}>文件夹</div>
            {folders.map((name) => (
              <div key={name} style={rowStyle}>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', wordBreak: 'break-word' }}>{name}</div>
                <button type="button" style={btn('secondary')} onClick={() => enterFolder(name)}>进入</button>
              </div>
            ))}
          </div>
        ) : null}

        {files.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 12, color: '#64748b' }}>文件</div>
            {files.map((r) => {
              const kind = guessKind(r);
              const allowed = isAllowed(r);
              return (
                <div key={r.objectName || r.fileName} style={rowStyle}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)', wordBreak: 'break-word' }}>{r.fileName || r.objectName}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                      {kind ? `类型：${kind.toUpperCase()}` : '类型：未知'}
                      {r.size ? ` · ${(Number(r.size) / 1024 / 1024).toFixed(1)}MB` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <button type="button" style={btn('secondary')} onClick={() => openFile(r)}>打开</button>
                    <button type="button" style={{ ...btn('primary'), opacity: allowed ? 1 : 0.5 }} disabled={!allowed} onClick={() => selectFile(r)}>
                      选择
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}

        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          支持类型：PDF / PPT / 视频（从文件管理选择）。
        </div>
      </div>
    </WideModal>
  );
}
